import {eventApi} from '~/CreateFabricInvitation/api/endpoints/event';
import {saveEventThunk} from '~/CreateFabricInvitation/api/event';
import {history} from '~/CreateFabricInvitation/history';
import {setInEditMode} from '~/CreateFabricInvitation/navigation/utils';
import {validateEventDetails} from '~/CreateFabricInvitation/navigation/validation';
import {selectIsDirty} from '~/CreateFabricInvitation/reducers/details/selectors';
import {canvasRemoveTextBordersAndIcons} from '~/common/canvas/customs/text_border';
import {HTTP_STATUS_PAYMENT_REQUIRED} from '~/common/fabric/constants';
import {
  eventStepsType,
  isInFreeAddGuestsStepExpVariant,
} from '~/common/fabric/EventGuests/components/DisplayCondition/addGuestsCondition';
import {
  PLAN_NAME_PARTY_PASS,
  PLAN_NAME_PREMIUM_PASS,
  PLAN_NAME_PREMIUM_PASS_SUBSCRIPTION,
  PLAN_NAME_PRO,
} from '~/common/fabric/routes/EventReview/constants';
import {isSubscriptionPlan} from '~/common/fabric/User/utils';
import {isFabricWebview} from '~/common/utils/webview';

export const sendEvent = async (store) => {
  const state = store.getState();
  return store.dispatch(eventApi.endpoints.send.initiate(state.eviteEvent.id));
};

const sendAndHandleError = async (store) => {
  const state = store.getState();
  const sendResponse = await sendEvent(store);
  if (sendResponse?.error?.status === HTTP_STATUS_PAYMENT_REQUIRED) {
    history.replace({
      pathname: `/${state.eviteEvent.id}/review`,
      hash: history.location.hash,
      search: history.location.search,
    });
    return false;
  }
  if (sendResponse?.error) {
    if (sendResponse.error.data?.message) {
      // when you cancel the captcha, there will be no data from the query,
      // so only show a warning to the user if we have an actual uncaught error
      evite.error(`Error sending event ${state.eviteEvent.id}`, sendResponse.error);
      throw new Error(sendResponse.error.data?.message);
    }
    return false;
  }
  return true;
};

const checkSaveAndSendBeforeLeave = async (store, route) => {
  if (route !== 'exit' && route !== 'previous') {
    const state = store.getState();
    const hasSub = isSubscriptionPlan(state?.user?.subscriptionPlanName, false);
    if (hasSub || window.eventHasProHost || state.eviteEvent.previously_purchased_offer) {
      await store.dispatch(saveEventThunk());
      const sentSuccessfully = await sendAndHandleError(store);
      if (!sentSuccessfully) return sentSuccessfully;
      history.replace({
        pathname: `/${state.eviteEvent.id}/confirm`,
        hash: history.location.hash,
        search: history.location.search,
      });
      return false;
    }
  }
  return true;
};

const preCheckoutButtonText = (store) => {
  const planName = store.getState().user.subscriptionPlanName;
  const hasPartyPass = planName === PLAN_NAME_PARTY_PASS;
  const hasPro = planName === PLAN_NAME_PRO;
  const hasPremiumPass =
    planName === PLAN_NAME_PREMIUM_PASS || planName === PLAN_NAME_PREMIUM_PASS_SUBSCRIPTION;
  let text = 'Next: Checkout';
  if (hasPremiumPass || hasPartyPass || window.eventHasProHost) {
    text = 'Finish & Send';
  } else if (hasPro) {
    text = 'Finish & Send with Evite Pro';
  }
  return text;
};

export const createSteps = {
  meta: {
    // Not a step - global settings for steps
    editing: false,
    creating: true,
    showBackButton: true,
    countSteps: true,
  },
  customize: {
    id: 1,
    name: 'customize',
    path: 'customize',
    displayName: 'Customize',
    title: 'Customize Your Invitation',
    nextRoute: 'details',
    previousRoute: null,
    saveOnLeave: true,
    nextButtonText: 'Next: Event Details',
    hideInWebView: false,
    preLeaveRouteFunction: (store, routeName, canvas) => canvasRemoveTextBordersAndIcons(canvas),
  },
  details: {
    id: 2,
    name: 'details',
    path: 'details',
    displayName: 'Edit Details',
    title: 'Enter Or Edit Your Event Details',
    nextRouteAvailableSelector: (state) => !selectIsDirty(state),
    preLeaveRouteFunction: validateEventDetails,
    nextRoute: 'gifting',
    previousRoute: 'customize',
    saveOnLeave: true,
    nextButtonText: 'Next: Gifting',
    hideInWebView: false,
  },
  gifting: {
    id: 3,
    path: 'gifting',
    name: 'gifting',
    displayName: 'Gifting Options',
    title: "Select one or more gift options you'd like to include in your invitation",
    nextRoute: 'preview',
    previousRoute: 'details',
    saveOnLeave: true,
    nextButtonText: 'Next: Review',
    hideInWebView: false,
  },
  preview: {
    id: 4,
    path: 'preview',
    name: 'preview',
    displayName: 'Review',
    title: `This Is A Review Of Your Invitation`,
    nextRoute: 'guests',
    previousRoute: 'gifting',
    saveOnLeave: true,
    nextButtonText: 'Next: Add Guests',
    hideInWebView: false,
    previewButtons: !isFabricWebview(),
  },
  guests: {
    id: 5,
    path: 'guests',
    name: 'guests',
    displayName: 'Add Guests',
    title: 'Add guests',
    nextRoute: 'review',
    preLeaveRouteFunction: async (store, route) => {
      // we could use checkSaveAndSendBeforeLeave but the next route is already
      // 'review', so lets just return true instead of redirecting prematurely
      if (route !== 'exit' && route !== 'previous') {
        const state = store.getState();
        const sendResponse = await sendEvent(store);
        if (sendResponse?.error?.status === HTTP_STATUS_PAYMENT_REQUIRED) {
          return true;
        }
        if (sendResponse?.error) {
          if (sendResponse.error.data?.message) {
            // when you cancel the captcha, there will be no data from the query,
            // so only show a warning to the user if we have an actual uncaught error
            evite.error(`Error sending event ${state.eviteEvent.id}`, sendResponse.error);
            throw new Error(sendResponse.error.data?.message);
          }
          return false;
        }
        history.replace({
          pathname: `/${state.eviteEvent.id}/confirm`,
          hash: history.location.hash,
          search: history.location.search,
        });
        return false;
      }
      return true;
    },
    previousRoute: 'preview',
    saveOnLeave: false,
    nextButtonText: (store) => {
      const state = store.getState();
      const hasSub = isSubscriptionPlan(state.user?.subscriptionPlanName, false);
      const isFreeEvent = !state.details.isPremium;
      return hasSub || window.eventHasProHost || isFreeEvent
        ? 'Send Invitation'
        : 'Next: Review & Send';
    },
    hideInWebView: true,
  },
  review: {
    id: 6,
    path: 'review',
    name: 'review',
    displayName: 'Review & Send',
    title: 'Review & Send',
    nextRoute: (store) => {
      const {details = {}} = store.getState();
      // if premium design or user has downgraded
      if (details.isPremium || details.hideUpsell || details.downgraded) {
        return 'confirm';
      }
      return 'send';
    },
    previousRoute: 'guests',
    saveOnLeave: false,
    nextButtonText: 'Purchase & Send', // TODO: Text changes: "Send Invitation" for free with guests. "Finish & Send Later" if free no guests.
    hideInWebView: true,
    isUpsellMode: (store) => !!store.getState().details.isUpsellMode,
    previewButtons: !isFabricWebview(),
  },
  send: {
    id: 7,
    path: 'send',
    name: 'send',
    displayName: 'Upgrade to Premium',
    title: 'Upgrade to Premium',
    preLeaveRouteFunction: async (store, route) => {
      // AB TEST: 'test_add-guests-step-free' - TO ROLL BACK: Remove this conditional check
      if (isInFreeAddGuestsStepExpVariant(store.getState())) return true; // Proceed without sending Invitation
      if (route !== 'exit') {
        const sentSuccessfully = await sendAndHandleError(store);
        if (!sentSuccessfully) return sentSuccessfully;
        await setInEditMode(store);
      }
      return true;
    },
    previousRoute: 'review',
    nextRoute: 'confirm',
    nextButtonText: 'Upgrade to Premium',
    hideInWebView: true,
    showSimpleNav: true,
    showFooter: false,
  },
  confirm: {
    id: 8,
    path: 'confirm',
    name: 'confirm',
    displayName: 'Confirmation',
    title: 'Confirmation',
    // nextRoute: 'confirm',
    previousRoute: 'review',
    saveOnLeave: false,
    // nextButtonText: 'Purchase & Send', // TODO: text changes for free/premium??
    hideInWebView: true,
    showMegaNav: true,
    showFooter: true,
  },
  exit: {
    // Routes us to the source page
    id: -1,
    path: !document.referrer?.includes?.('login') && document.referrer ? document.referrer : '/',
    hideInWebView: true,
  },
};

export const freemiumSteps = {
  ...createSteps,
  preview: {
    ...createSteps.preview,
    nextRoute: createSteps.guests.nextRoute,
    preLeaveRouteFunction: checkSaveAndSendBeforeLeave,
    nextButtonText: preCheckoutButtonText,
  },
  review: {
    ...createSteps.review,
    id: createSteps.guests.id,
    displayName: 'Checkout',
    previousRoute: createSteps.guests.previousRoute,
    nextButtonText: 'Purchase & Add Guests',
  },
};

export const freeSteps = {
  ...createSteps,
  review: {
    ...createSteps.review,
    id: 5,
  },
  preview: {
    ...createSteps.preview,
    name: 'preview',
    previousRoute: 'gifting',
    preLeaveRouteFunction: async (store, routeName) => {
      if (['send', 'confirm'].includes(routeName)) {
        const sendResponse = await sendEvent(store);
        return !sendResponse?.error;
      }
      return true;
    },
    nextRoute: createSteps.review.nextRoute,
    nextButtonText: 'Finish & Add Guests',
  },
};

export const freeWithGuestsSteps = {
  ...freeSteps,
  preview: {
    ...freeSteps.preview,
    nextRoute: 'guests',
    nextButtonText: 'Next: Add Guests',
    preLeaveRouteFunction: null,
  },
  guests: {
    ...freeSteps.guests,
    nextRoute: createSteps.review.nextRoute,
    nextButtonText: 'Finish & Send',
    preLeaveRouteFunction: freeSteps.preview.preLeaveRouteFunction,
  },
};

// AB TEST: 'test_add-guests-step-free' - TO ROLL BACK: Remove this entire step configuration
export const freeWithGuestsABSteps = {
  ...freeWithGuestsSteps,
  preview: {
    ...freeWithGuestsSteps.preview,
    nextRoute: (store) =>
      store.getState().details.isPremium || store.getState().details.downgraded ? 'guests' : 'send',
  },
  guests: {
    ...freeWithGuestsSteps.guests,
    nextRoute: 'confirm',
  },
  send: {
    ...freeWithGuestsSteps.send,
    nextRoute: 'preview',
    saveOnLeave: false,
  },
};

export const freemiumWithGuestsSteps = {
  ...freemiumSteps,
  preview: {
    ...freemiumSteps.preview,
    nextRoute: createSteps.preview.nextRoute,
    nextButtonText: createSteps.preview.nextButtonText,
    preLeaveRouteFunction: null,
  },
  guests: {
    ...createSteps.guests,
    preLeaveRouteFunction: checkSaveAndSendBeforeLeave,
    nextButtonText: preCheckoutButtonText,
  },
  review: {
    ...createSteps.review,
    displayName: 'Checkout',
    nextButtonText: 'Purchase & Send',
  },
};

export const premiumAddGuestsAfterSteps = {
  ...createSteps,
  preview: {
    ...createSteps.preview,
    nextRoute: createSteps.guests.nextRoute,
    preLeaveRouteFunction: checkSaveAndSendBeforeLeave,
    nextButtonText: preCheckoutButtonText,
  },
  review: {
    ...createSteps.review,
    id: createSteps.guests.id,
    displayName: 'Checkout',
    previousRoute: createSteps.guests.previousRoute,
    nextButtonText: 'Purchase & Add Guests',
  },
};

export const numSteps = 6;

export const eventStepCount = {
  [eventStepsType.PREMIUM]: numSteps,
  [eventStepsType.PREMIUM_ADD_GUESTS_AFTER]: numSteps - 1,
  [eventStepsType.FREEMIUM_WITH_GUESTS]: numSteps,
  [eventStepsType.FREEMIUM]: numSteps - 1,
  [eventStepsType.FREE_WITH_GUESTS]: numSteps - 1,
  // AB TEST: 'test_add-guests-step-free' - TO ROLL BACK: Remove this line
  [eventStepsType.FREE_WITH_GUESTS_AB]: numSteps - 1,
  [eventStepsType.FREE]: numSteps - 2,
};

export const eventSteps = {
  [eventStepsType.PREMIUM]: createSteps,
  [eventStepsType.PREMIUM_ADD_GUESTS_AFTER]: premiumAddGuestsAfterSteps,
  [eventStepsType.FREEMIUM_WITH_GUESTS]: freemiumWithGuestsSteps,
  [eventStepsType.FREEMIUM]: freemiumSteps,
  [eventStepsType.FREE_WITH_GUESTS]: freeWithGuestsSteps,
  // AB TEST: 'test_add-guests-step-free' - TO ROLL BACK: Remove this line
  [eventStepsType.FREE_WITH_GUESTS_AB]: freeWithGuestsABSteps,
  [eventStepsType.FREE]: freeSteps,
};
